import React, { useState } from "react";
import styled from "@emotion/styled";

import {
  Button,
  InputAdornment,
  Popover,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import Icon from "@imminently/decisively-ui/Icon";
import DeciSelect from "@imminently/decisively-ui/Select";

import { Tooltip } from "@/components/toolTip";

/**
 * @type { React.FC< import("@mui/material/TextField").TextFieldProps & { action?: any; inputPointerEventsNone?: true } > }
 * @deprecated Use @imminently/decisively-ui/TextField instead
 */
export const TextField = ({ icon, action, inputPointerEventsNone, ref, ...props }) => {
  const inputProps = {
    startAdornment: icon ? <InputAdornment position="start">{icon}</InputAdornment> : undefined,
    endAdornment: action ? <InputAdornment position="end">{action}</InputAdornment> : undefined,
  };

  return <MuiTextField
    innerRef={ref}
    color="secondary"
    variant="outlined"
    InputProps={inputProps}
    inputProps={inputPointerEventsNone && { style: { pointerEvents: "none" } }}
    {...props}
  />;
};

/** @deprecated */
export const SelectButton = styled(Button)`
  font-weight: 400;
  background-color: var(--background);
  transition-property: background-color;

  &:hover {
    background-color: var(--background) !important;
    box-shadow: inset 0 0 0px 1px;
  }

  .MuiButton-startIcon {
    margin-left: 0;
  }

  .MuiButton-endIcon {
    margin-left: auto;
    margin-right: 0;
  }
`;

/**
 * @type { React.FC< any > }
 * @deprecated
 */
export const DropdownMenu = ({
  showFullOnHover,
  value,
  icon,
  anchorOrigin,
  transformOrigin,
  style,
  children,
  fullWidth,
  className,
  placeholder,
  popoverProps,
  renderComponent = "button",
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  anchorOrigin = anchorOrigin || {
    vertical: "bottom",
    horizontal: "left",
  };

  transformOrigin = transformOrigin || {
    vertical: "top",
    horizontal: "left",
  };

  const childProps = {
    close: () => handleClose(),
  };

  const open = Boolean(anchorEl);

  const buttonStyle = open ? {
    ...style,
    boxShadow: "inset 0 0 0px 1px",
  } : style;

  const placeholderText = placeholder ?
    <Typography variant="button" display="block" color="textSecondary" noWrap>
      {placeholder}
    </Typography> : null;

  const button = <SelectButton
    disableRipple
    color="secondary"
    variant="contained"
    startIcon={icon}
    endIcon={open ? <Icon name="collapse" /> : <Icon name="expand" />}
    onClick={handleClick}
    style={buttonStyle}
    {...props}
  >
    {
      value
        ? <Typography variant="button" display="block" noWrap>{value}</Typography>
        : placeholderText
    }
  </SelectButton>;

  const textField =
    <div
      className={className}
      style={style}
      onClick={handleClick}
    >
      <DeciSelect
        value={value ? String(value) : null}
        options={value ? [{ value: String(value), label: String(value) }] : []}
        placeholder={typeof placeholder === "string" ? placeholder : ""}
        searchable={false}
        menuIsOpen={false}
        openMenuOnClick={false}
        openMenuOnFocus={false}
        onValueChange={() => {}}
        className="w-full text-left "
      />
    </div>;

  const renderComponentFn = (() => {
    switch (renderComponent) {
      case "textfield":
        return textField;
      case "button":
      default:
        return button;
    }
  })();

  return (
    <>
      {value && showFullOnHover ? <Tooltip interactive={true} title={value} enterDelay={500}>
        {renderComponentFn}
      </Tooltip> : renderComponentFn}
      <Popover
        id="menu"
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        {...popoverProps}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        keepMounted
        open={open}
        onClose={handleClose}
      >
        {typeof children === "function" ? children(childProps) : children}
      </Popover>
    </>
  );
};
